/**
 * Coalesces bursty updates down to the latest payload per animation frame.
 *
 * Live transform previews are expensive enough that letting pointer events
 * drive them at raw device rate causes work to backlog on dense scenes,
 * especially on mobile Safari. Keeping the latest payload and flushing at
 * most once per frame gives every drag surface the same bounded contract.
 */

export interface RafCoalescer<T> {
  schedule(payload: T): void;
  flush(): void;
  cancel(): void;
}

export function createRafCoalescer<T>(dispatch: (payload: T) => void): RafCoalescer<T> {
  let rafId: number | null = null;
  let pending: T | null = null;

  const run = () => {
    rafId = null;
    const next = pending;
    pending = null;
    if (next !== null) dispatch(next);
  };

  return {
    schedule(payload: T) {
      pending = payload;
      if (rafId === null) {
        rafId = requestAnimationFrame(run);
      }
    },
    flush() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      run();
    },
    cancel() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      pending = null;
    },
  };
}
